import { Form, Link, useActionData, useNavigation } from "react-router";
import { useState, useCallback, useRef, type KeyboardEvent } from "react";
import { VoiceInput } from "~/components/voice-input";
import { usePhoneticResolver } from "~/hooks/use-phonetic-resolver";
import { useVoiceToText } from "~/hooks/use-voice-to-text";

type AppUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
};

type ConvertWorkbenchProps = {
  user?: AppUser | null;
  history?: Array<any>;
  showAuthHeader?: boolean;
  showExamples?: boolean;
  showHistory?: boolean;
  headerTitle?: string;
  headerDescription?: string;
};

export function ConvertWorkbench({
  user = null,
  history = [],
  showAuthHeader = false,
  showExamples = false,
  showHistory = true,
  headerTitle = "Fluentry",
  headerDescription = "Transform mixed-language input into natural English",
}: ConvertWorkbenchProps) {
  const actionData = useActionData() as any;
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const [inputText, setInputText] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    activeReading,
    candidates,
    clearCandidates,
    commitSelection,
    isLoading: isResolvingPhonetic,
    resolveCandidates,
    selectedIndex,
    selectNextCandidate,
    selectPreviousCandidate,
    setSelectedIndex,
  } = usePhoneticResolver();

  const { transcribeAudio, isProcessing } = useVoiceToText({
    onTranscription: (text) => {
      setInputText(text);
      clearCandidates();
      if (textareaRef.current) {
        textareaRef.current.value = text;
      }
    },
    onError: (error) => {
      console.error("Voice recognition error:", error);
    },
  });

  const handleSpeechEnd = useCallback(
    async (audioData: Float32Array) => {
      try {
        await transcribeAudio(audioData);
      } catch (error) {
        console.error("Failed to transcribe audio:", error);
      }
    },
    [transcribeAudio],
  );

  const applyCandidate = useCallback(
    (index?: number) => {
      const current = textareaRef.current;
      const caretPosition = current?.selectionStart ?? inputText.length;
      const committed = commitSelection(inputText, caretPosition, index);
      if (!committed.replaced) {
        return;
      }

      setInputText(committed.text);
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(committed.caretPosition, committed.caretPosition);
      });
    },
    [commitSelection, inputText],
  );

  const handleTextChange = useCallback(
    (nextText: string, caretPosition: number) => {
      setInputText(nextText);
      void resolveCandidates({
        text: nextText,
        caretPosition,
        contextText: nextText,
        isComposing,
        maxCandidates: 3,
      });
    },
    [isComposing, resolveCandidates],
  );

  const handleTextareaKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (candidates.length === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        selectNextCandidate();
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        selectPreviousCandidate();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        clearCandidates();
        return;
      }

      if (event.key === "Tab" || event.key === "Enter") {
        event.preventDefault();
        applyCandidate();
      }
    },
    [
      applyCandidate,
      candidates.length,
      clearCandidates,
      selectNextCandidate,
      selectPreviousCandidate,
    ],
  );

  return (
    <div className="relative">
      {showAuthHeader && (
        <div className="absolute top-4 right-4 z-10">
          {user ? (
            <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-indigo-100">
              {user.image ? (
                <img
                  src={user.image}
                  alt={user.name || "User"}
                  className="w-8 h-8 rounded-full border border-gray-200"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold border border-indigo-200">
                  {user.email?.[0]?.toUpperCase() || "U"}
                </div>
              )}
              <div className="text-sm">
                <p className="font-medium text-gray-800">{user.name || user.email?.split("@")[0]}</p>
              </div>
              <div className="h-4 w-px bg-gray-300 mx-1"></div>
              <Form action="/api/auth/signout" method="post">
                <button
                  type="submit"
                  className="text-sm text-gray-500 hover:text-red-600 font-medium transition-colors"
                >
                  Sign Out
                </button>
              </Form>
            </div>
          ) : (
            <Link
              to="/auth/signin"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 shadow-sm ring-1 ring-indigo-100 transition-colors"
            >
              Sign In
            </Link>
          )}
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{headerTitle}</h1>
          <p className="text-xl text-gray-600 mb-6">{headerDescription}</p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
            <span className="bg-white px-3 py-1 rounded-full">📝 Mixed Language → English</span>
            <span className="bg-white px-3 py-1 rounded-full">🎯 3 Style Variations</span>
            <span className="bg-white px-3 py-1 rounded-full">⚡ AI-Powered</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <Form method="post" className="space-y-6">
            <div>
              <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-2">
                Enter mixed-language text:
              </label>
              <textarea
                ref={textareaRef}
                id="text"
                name="text"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="例: I want to 休む today because I'm とても tired..."
                disabled={isSubmitting || isProcessing}
                value={inputText}
                onChange={(e) => {
                  const caret = e.currentTarget.selectionStart ?? e.currentTarget.value.length;
                  handleTextChange(e.target.value, caret);
                }}
                onKeyDown={handleTextareaKeyDown}
                onCompositionStart={() => {
                  setIsComposing(true);
                  clearCandidates();
                }}
                onCompositionEnd={(e) => {
                  setIsComposing(false);
                  const caret = e.currentTarget.selectionStart ?? e.currentTarget.value.length;
                  void resolveCandidates({
                    text: e.currentTarget.value,
                    caretPosition: caret,
                    contextText: e.currentTarget.value,
                    isComposing: false,
                    maxCandidates: 3,
                  });
                }}
              />
              {(candidates.length > 0 || isResolvingPhonetic) && (
                <div className="mt-2 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2">
                  <div className="mb-1 text-xs text-indigo-700">
                    {activeReading ? `PhoneticResolver候補: ${activeReading}` : "PhoneticResolver候補"}
                  </div>
                  {isResolvingPhonetic && candidates.length === 0 ? (
                    <p className="text-sm text-indigo-500">候補を取得中...</p>
                  ) : (
                    <ul
                      className="space-y-1"
                      role="listbox"
                      aria-label="PhoneticResolver candidates"
                      data-testid="phonetic-candidate-list"
                    >
                      {candidates.map((candidate, index) => (
                        <li key={`${candidate.text}-${index}`}>
                          <button
                            type="button"
                            className={`w-full rounded px-2 py-1 text-left text-sm transition-colors ${
                              selectedIndex === index
                                ? "bg-indigo-600 text-white"
                                : "bg-white text-indigo-900 hover:bg-indigo-100"
                            }`}
                            onMouseEnter={() => setSelectedIndex(index)}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              applyCandidate(index);
                            }}
                            role="option"
                            aria-selected={selectedIndex === index}
                            data-testid={`phonetic-candidate-${index}`}
                          >
                            <span>{candidate.text}</span>
                            <span className="ml-2 text-xs opacity-80">
                              {(candidate.confidence * 100).toFixed(0)}%
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="includeExplanation"
                  name="includeExplanation"
                  type="checkbox"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="includeExplanation" className="ml-2 block text-sm text-gray-700">
                  Include explanation (for learning)
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">or</span>
                <VoiceInput onSpeechEnd={handleSpeechEnd} />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || isProcessing}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              {isSubmitting
                ? "Converting..."
                : isProcessing
                  ? "Processing voice..."
                  : "Convert to English"}
            </button>
          </Form>
        </div>

        {actionData && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            {"error" in actionData ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      {"error" in actionData ? actionData.error : ""}
                    </div>
                  </div>
                </div>
              </div>
            ) : "result" in actionData ? (
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Conversion Results</h2>
                <div className="border-l-4 border-green-400 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">😊 Casual</h3>
                  <p className="text-gray-700">
                    {"result" in actionData ? actionData.result.casual : ""}
                  </p>
                </div>
                <div className="border-l-4 border-blue-400 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">🏢 Formal</h3>
                  <p className="text-gray-700">
                    {"result" in actionData ? actionData.result.formal : ""}
                  </p>
                </div>
                <div className="border-l-4 border-purple-400 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">💬 Conversational</h3>
                  <p className="text-gray-700">
                    {"result" in actionData ? actionData.result.conversational : ""}
                  </p>
                </div>
                {"result" in actionData && actionData.result.explanation && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">📚 Explanation</h3>
                    <p className="text-gray-700">
                      {"result" in actionData ? actionData.result.explanation : ""}
                    </p>
                  </div>
                )}
                {"result" in actionData && actionData.result.confidence && (
                  <div className="text-sm text-gray-500">
                    Confidence:{" "}
                    {"result" in actionData && actionData.result.confidence
                      ? Math.round(actionData.result.confidence * 100)
                      : 0}
                    %
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {showHistory && history && history.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent History</h2>
            <div className="space-y-4">
              {history.map((item: any) => (
                <div key={item.id} className="border-b border-gray-100 pb-3 last:border-0">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    {new Date(item.createdAt).toLocaleString()}
                  </p>
                  <p className="text-gray-800 italic mb-2">"{item.input}"</p>
                  <p className="text-gray-600 text-sm">➜ {item.casualOutput}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {showExamples && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Examples</h2>
            <div className="space-y-3">
              <div className="text-sm">
                <span className="font-medium text-gray-600">Input:</span> "I want to 休む today"
                <br />
                <span className="font-medium text-gray-600">Output:</span> "I want to take a break
                today"
              </div>
              <div className="text-sm">
                <span className="font-medium text-gray-600">Input:</span> "This meeting is とても
                important"
                <br />
                <span className="font-medium text-gray-600">Output:</span> "This meeting is very
                important"
              </div>
              <div className="text-sm">
                <span className="font-medium text-gray-600">Input:</span> "Can you help me with this
                宿題?"
                <br />
                <span className="font-medium text-gray-600">Output:</span> "Can you help me with
                this homework?"
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

